import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, Loader2 } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import ProductCard from '../components/ProductCard';
import { getProductCategoryFilters } from '../lib/productCategories';
import { fetchProducts } from '../api/services';
import { useSettings } from '../context/SettingsContext';
import SectionRenderer from '../components/page-builder/SectionRenderer';
import { getPage } from '../lib/pageDefaults';

export default function Products() {
  const { settings } = useSettings();
  const page = getPage(settings?.page_builder, '/produk') || {};
  const categories = getProductCategoryFilters(settings);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('kategori') || 'all';
  const [category, setCategory] = useState(initialCat);
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCategory(searchParams.get('kategori') || 'all');
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchProducts({ category: category === 'all' ? undefined : category, search: query || undefined })
      .then((data) => { if (alive) setProducts(data); })
      .catch(() => { if (alive) setProducts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, query]);

  const handleCategoryChange = (cat) => {
    setCategory(cat);
    if (cat === 'all') setSearchParams({});
    else setSearchParams({ kategori: cat });
  };

  return (
    <>
      <PageBanner
        title={page.title || "Produk Bubuk Minuman"}
        subtitle={page.subtitle || "Temukan varian rasa premium terlengkap dari BelBel untuk bisnis minuman Anda."}
        breadcrumb={['Produk']}
      />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-6 mb-10 border border-gray-100">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-japo-green" />
                <span className="font-semibold text-sm mr-2">Kategori:</span>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleCategoryChange(c.id)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      category === c.id
                        ? 'bg-japo-green text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-rose-50 border border-gray-200'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari varian rasa..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 bg-white text-sm"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat produk...
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">Tidak ada produk ditemukan.</p>
            </div>
          ) : (
            <>
              <div className="mb-6 text-sm text-gray-600">
                Menampilkan <span className="font-bold text-japo-green">{products.length}</span> produk
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <SectionRenderer sections={page.sections || []} />
    </>
  );
}
